import { USER_TYPES, MESSAGE_STATUS, MESSAGE_ITEM_TYPES } from "./chatConstants";

const checkIsSender = (message, currentUserId, currentCompanyId) => {
  return message.senderId === currentUserId || message.senderCompanyId === currentCompanyId;
};

const determineChatPartner = (message, isSender, t) => {
  const isDriverMessage = message.userType === "Driver";
  const isClientMessage = message.userType === "Client";

  if (isSender) {
    // Определяем партнёра по получателю
    if (isDriverMessage || !message.receiverCompanyId) {
      return {
        id: message.receiverId,
        name: message.receiverName || t("orderInfo.ordersDriverInfo.driverLabel"),
        companyName: message.receiverCompanyName,
        companyId: message.receiverCompanyId,
        type: USER_TYPES.DRIVER,
      };
    }

    if (isClientMessage) {
      return {
        id: message.receiverCompanyId,
        name: message.receiverCompanyName || t("registrationSurveyUserSecond.orgType.shipper"),
        companyName: message.receiverCompanyName,
        companyId: message.receiverCompanyId,
        type: USER_TYPES.CLIENT,
      };
    }

    // Expeditor или неизвестный тип - определяем по наличию companyId
    return {
      id: message.receiverCompanyId,
      name: message.receiverCompanyName || t("registrationSurveyUserSecond.orgType.shipper"),
      companyName: message.receiverCompanyName,
      companyId: message.receiverCompanyId,
      type: USER_TYPES.CLIENT,
    };
  }

  // Определяем партнёра по отправителю
  if (isDriverMessage) {
    return {
      id: message.senderId,
      name: message.senderName || t("orderInfo.ordersDriverInfo.driverLabel"),
      companyName: message.senderCompanyName,
      companyId: message.senderCompanyId,
      type: USER_TYPES.DRIVER,
    };
  }

  if (isClientMessage) {
    return {
      id: message.senderCompanyId,
      name: message.senderCompanyName || t("registrationSurveyUserSecond.orgType.shipper"),
      companyName: message.senderCompanyName,
      companyId: message.senderCompanyId,
      type: USER_TYPES.CLIENT,
    };
  }

  // Expeditor
  return {
    id: message.senderCompanyId,
    name: message.senderCompanyName || t("orderChat.company"),
    companyName: message.senderCompanyName,
    companyId: message.senderCompanyId,
    type: USER_TYPES.EXPEDITOR,
  };
};

const getChatGroupId = (chatPartner) => {
  return chatPartner.type === USER_TYPES.DRIVER ? chatPartner.id : chatPartner.companyId || chatPartner.id;
};

const getRoleLabel = (type, t) => {
  const roleLabels = {
    [USER_TYPES.DRIVER]: t("orderInfo.ordersDriverInfo.driverLabel"),
    [USER_TYPES.EXPEDITOR]: t("registrationSurveyUserSecond.orgType.transporter"),
    [USER_TYPES.CLIENT]: t("registrationSurveyUserSecond.orgType.shipper"),
  };
  return roleLabels[type] || t("orderChat.participant");
};

const formatTime = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

const getMessageStatus = (message, currentUserId, currentCompanyId) => {
  const isSender = checkIsSender(message, currentUserId, currentCompanyId);

  if (!isSender) return null;

  const receiverId = message.receiverId;
  const receiverCompanyId = message.receiverCompanyId;

  if (message.readBy && message.readBy.length > 0) {
    if (message.readBy.includes(receiverId) || message.readBy.includes(receiverCompanyId)) {
      return MESSAGE_STATUS.READ;
    }
  }

  return MESSAGE_STATUS.DELIVERED;
};

const formatDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (date, t) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateString = formatDateOnly(date);
  const todayString = formatDateOnly(today);
  const yesterdayString = formatDateOnly(yesterday);

  if (dateString === todayString) {
    return t("common.datetime.today");
  } else if (dateString === yesterdayString) {
    return t("common.datetime.yesterday");
  } else {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }
};

export const groupChatMessages = (messages, currentUserId, currentCompanyId, t) => {
  if (!messages || !Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  const chatGroups = {};

  messages.forEach((message) => {
    const isSender = checkIsSender(message, currentUserId, currentCompanyId);
    const chatPartner = determineChatPartner(message, isSender, t);
    const chatGroupId = getChatGroupId(chatPartner);

    if (!chatGroups[chatGroupId]) {
      chatGroups[chatGroupId] = {
        id: chatPartner.id,
        name: chatPartner.name,
        role: getRoleLabel(chatPartner.type, t),
        type: chatPartner.type,
        companyName: chatPartner.companyName,
        companyId: chatPartner.companyId,
        messages: [],
        lastMessage: "",
        time: "",
        unreadCount: 0,
      };
    }

    const formattedMessage = {
      id: message.id,
      text: message.text,
      messageType: String(message.messageType || "text").toLowerCase(),
      sent: isSender,
      time: formatTime(message.createdAt),
      status: getMessageStatus(message, currentUserId, currentCompanyId),
      isRead: message.isRead,
      isSystem: message.isSystem === true,
      createdAt: message.createdAt,
    };

    chatGroups[chatGroupId].messages.push(formattedMessage);

    if (
      !chatGroups[chatGroupId].lastMessageDate ||
      new Date(message.createdAt) > new Date(chatGroups[chatGroupId].lastMessageDate)
    ) {
      chatGroups[chatGroupId].lastMessage = message.text;
      chatGroups[chatGroupId].time = formatTime(message.createdAt);
      chatGroups[chatGroupId].lastMessageDate = message.createdAt;
    }

    if (!isSender && !message.isRead) {
      chatGroups[chatGroupId].unreadCount++;
    }
  });

  const groupedChats = Object.values(chatGroups).map((chat) => {
    chat.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return chat;
  });

  groupedChats.sort((a, b) => new Date(b.lastMessageDate) - new Date(a.lastMessageDate));

  return groupedChats;
};

export const addDateSeparators = (messages, t) => {
  if (!messages || messages.length === 0) return [];

  const messagesWithSeparators = [];
  let lastDate = null;

  messages.forEach((message) => {
    const messageDate = new Date(message.createdAt);
    const messageDateString = formatDateOnly(messageDate);

    if (lastDate !== messageDateString) {
      messagesWithSeparators.push({
        id: `separator-${messageDateString}`,
        type: MESSAGE_ITEM_TYPES.DATE_SEPARATOR,
        date: messageDateString,
        displayDate: formatDateLabel(messageDate, t),
      });
      lastDate = messageDateString;
    }

    messagesWithSeparators.push(message);
  });

  return messagesWithSeparators;
};
